import type { ResolvedModalContentChooserOptions } from '../options';
import { ModalContentCollection } from './ModalContentCollection';
import { ModalContentState } from './ModalContentState';
import type { ModalContentItem } from './types';

/**
 * // Class Obj: モーダル内の選択リストを管理
 * // Note:
 * // - data-modal-choose-list を描画先として、現在 group に属する item を一覧化します。
 * // - data-modal-choose-item のクリックはイベント委譲で受け取り、生成後の item にも対応します。
 * // - 選択リストは画像・動画に限定せず、data-modal-choose-source または content source を複製して作成します。
 * // - options により、全体または group 単位で選択リストを有効 / 無効にできます。
 */
export class ModalContentChooser
{
  private static readonly SELECTOR_CHOOSE_LIST = '[data-modal-choose-list]';
  private static readonly SELECTOR_CHOOSE_ITEM = '[data-modal-choose-item]';
  private static readonly SELECTOR_CONTENT_DISPLAY = '[data-modal-content-display]';
  private static readonly SELECTOR_CHOOSE_AREA = '.choose';

  private static readonly ATTR_CONTENT_GROUP = 'data-modal-content-group';
  private static readonly ATTR_CONTENT_INDEX = 'data-modal-content-index';
  private static readonly ATTR_CHOOSE_ITEM = 'data-modal-choose-item';
  private static readonly ATTR_CHOOSE_LIST_ITEM = 'data-modal-choose-list-item';
  private static readonly ATTR_CHOOSE_SCROLL_STATE = 'data-modal-choose-scroll-state';
  private static readonly ATTR_CHOOSE_SCROLL_POSITION = 'data-modal-choose-scroll-position';
  private static readonly ATTR_CHOOSE_AREA = 'data-modal-choose';
  private static readonly ATTR_CHOOSE_ITEM_STATE = 'data-modal-choose-item-state';

  private static readonly CLASS_CHOOSE_AREA = 'choose';
  private static readonly CLASS_CHOOSE_LIST = 'modal-choose-list';
  private static readonly CLASS_LIST_ITEM = 'item';
  private static readonly CLASS_CHOOSE_BUTTON = 'btn-modal-choose';

  private readonly mContentCollection: ModalContentCollection;
  private readonly mContentState: ModalContentState;
  private readonly mChooseClickListeners: Map<HTMLElement, (event: MouseEvent) => void> = new Map();
  private readonly mScrollStateListeners: Map<HTMLElement, () => void> = new Map();
  private readonly mGeneratedChooseAreas: Set<HTMLElement> = new Set();
  private readonly mGeneratedChooseLists: Set<HTMLElement> = new Set();
  private mOptions: ResolvedModalContentChooserOptions;

  /**
   * // Method: 選択リスト管理に必要な依存クラスと設定を保持する
   */
  constructor(
    content_collection: ModalContentCollection,
    content_state: ModalContentState,
    options: ResolvedModalContentChooserOptions,
  )
  {
    this.mContentCollection = content_collection;
    this.mContentState = content_state;
    this.mOptions = options;
  }

  /**
   * // Method: 必要に応じてモーダル内の choose list UI を自動生成する
   * // Note:
   * // - 既に data-modal-choose-list がある場合は、手書きmarkupを優先して何も追加しません。
   * // - choose.mode: 'chooser-viewer' の場合だけ生成し、single-viewer では生成しません。
   * // - responsive で chooser-viewer へ切り替わった場合も、再同期時に生成します。
   */
  public createLists(target_modals: HTMLElement[]): void
  {
    if (!this.mOptions.autoCreateList)
    {
      return;
    }

    target_modals.forEach((modal: HTMLElement) =>
    {
      this.createListIfNeeded(modal);
    });
  }

  /**
   * // Method: responsive 再解決後の choose option を反映する
   */
  public updateOptions(options: ResolvedModalContentChooserOptions): void
  {
    this.mOptions = options;
  }

  /**
   * // Method: 指定モーダルに choose list がなければ生成する
   */
  private createListIfNeeded(modal: HTMLElement): void
  {
    if (this.getChooseList(modal))
    {
      return;
    }

    const choose_area: HTMLElement | null = this.resolveChooseArea(modal);

    if (!choose_area)
    {
      return;
    }

    const choose_list: HTMLUListElement = this.createChooseListElement();

    choose_area.appendChild(choose_list);
    this.mGeneratedChooseLists.add(choose_list);
  }

  /**
   * // Method: choose list の配置先を取得または作成する
   * // Note:
   * // - 既存の .choose があればそこへ追加します。
   * // - .choose がない場合は data-modal-content-display の直後に生成し、提示済みmarkupに近いDOM順にします。
   */
  private resolveChooseArea(modal: HTMLElement): HTMLElement | null
  {
    const current_choose_area: HTMLElement | null = modal.querySelector<HTMLElement>(
      ModalContentChooser.SELECTOR_CHOOSE_AREA,
    );

    if (current_choose_area)
    {
      return current_choose_area;
    }

    const choose_area: HTMLDivElement = document.createElement('div');
    choose_area.classList.add(ModalContentChooser.CLASS_CHOOSE_AREA);
    choose_area.setAttribute(ModalContentChooser.ATTR_CHOOSE_AREA, '');
    this.mGeneratedChooseAreas.add(choose_area);

    const content_display: HTMLElement | null = modal.querySelector<HTMLElement>(
      ModalContentChooser.SELECTOR_CONTENT_DISPLAY,
    );

    if (content_display)
    {
      content_display.insertAdjacentElement('afterend', choose_area);
      return choose_area;
    }

    modal.appendChild(choose_area);

    return choose_area;
  }

  /**
   * // Method: 自動生成用の choose list 要素を作成する
   */
  private createChooseListElement(): HTMLUListElement
  {
    const choose_list: HTMLUListElement = document.createElement('ul');

    choose_list.classList.add(ModalContentChooser.CLASS_CHOOSE_LIST);
    choose_list.setAttribute('data-modal-choose-list', '');
    choose_list.hidden = true;
    choose_list.setAttribute('aria-hidden', 'true');

    return choose_list;
  }

  /**
   * // Method: モーダル内の choose item クリックをイベント委譲で登録する
   * // Note:
   * // - choose list を描画し直しても、イベント登録をやり直す必要がない構造にしています。
   * // - 手書きの data-modal-choose-item と、JSで生成した data-modal-choose-item の両方に対応します。
   * // - options で無効な group の choose item は、クリックされても表示切替しません。
   */
  public bind(
    target_modals: HTMLElement[],
    callback: (content_item: ModalContentItem, modal: HTMLElement) => void,
  ): void
  {
    target_modals.forEach((modal: HTMLElement) =>
    {
      this.unbindChooseClickByModal(modal);

      const click_listener = (event: MouseEvent): void =>
      {
        const target_element: Element | null = event.target instanceof Element ? event.target : null;

        if (!target_element)
        {
          return;
        }

        const choose_item: HTMLElement | null = target_element.closest<HTMLElement>(
          ModalContentChooser.SELECTOR_CHOOSE_ITEM,
        );

        if (!choose_item || !modal.contains(choose_item))
        {
          return;
        }

        const content_item: ModalContentItem | null = this.getContentItemByChooseItem(choose_item);

        if (!content_item || !this.isGroupEnabled(content_item.group_name))
        {
          return;
        }

        event.preventDefault();

        callback(content_item, modal);
      };

      modal.addEventListener('click', click_listener);
      this.mChooseClickListeners.set(modal, click_listener);
    });
  }

  /**
   * // Method: choose list 関連のイベント・自動生成DOM・状態属性を解除する
   */
  public destroy(target_modals: HTMLElement[]): void
  {
    target_modals.forEach((modal: HTMLElement) =>
    {
      this.unbindChooseClickByModal(modal);

      const choose_list: HTMLElement | null = this.getChooseList(modal);

      if (choose_list)
      {
        this.clearChooseList(choose_list);
        this.unbindChooseListScrollState(choose_list);
      }
    });

    Array.from(this.mScrollStateListeners.keys()).forEach((choose_list: HTMLElement) =>
    {
      this.unbindChooseListScrollState(choose_list);
    });

    this.mGeneratedChooseLists.forEach((choose_list: HTMLElement) =>
    {
      choose_list.remove();
    });
    this.mGeneratedChooseLists.clear();

    this.mGeneratedChooseAreas.forEach((choose_area: HTMLElement) =>
    {
      choose_area.remove();
    });
    this.mGeneratedChooseAreas.clear();
  }

  /**
   * // Method: 指定モーダルに登録した choose click event を解除する
   */
  private unbindChooseClickByModal(modal: HTMLElement): void
  {
    const click_listener: ((event: MouseEvent) => void) | undefined =
      this.mChooseClickListeners.get(modal);

    if (!click_listener)
    {
      return;
    }

    modal.removeEventListener('click', click_listener);
    this.mChooseClickListeners.delete(modal);
  }

  /**
   * // Method: 現在表示中 item に合わせて選択リストを更新する
   * // Note:
   * // - data-modal-choose-list がないモーダルでは何も行いません。
   * // - group 移動時も item 移動時も、このメソッドを呼ぶだけで current 表示まで同期します。
   * // - options で無効な場合、描画済みの選択リストも空にして非表示にします。
   */
  public update(modal: HTMLElement | null, current_item: ModalContentItem): void
  {
    const choose_list: HTMLElement | null = this.getChooseList(modal);

    if (!choose_list)
    {
      return;
    }

    if (!this.shouldShowByItem(current_item))
    {
      this.clearChooseList(choose_list);
      return;
    }

    const group_items: ModalContentItem[] = this.mContentCollection.getItemsByGroup(
      current_item.group_name,
    );

    if (this.shouldHideByItemCount(group_items.length))
    {
      this.clearChooseList(choose_list);
      return;
    }

    choose_list.replaceChildren(
      ...group_items.map((content_item: ModalContentItem) =>
      {
        return this.createListItem(content_item, current_item);
      }),
    );

    choose_list.hidden = false;
    choose_list.toggleAttribute('aria-hidden', false);
    this.bindChooseListScrollState(choose_list);
    this.syncChooseListScrollState(choose_list, group_items.length);
  }

  /**
   * // Method: モーダル内の選択リストを空にする
   */
  public clear(target_modals: HTMLElement[]): void
  {
    target_modals.forEach((modal: HTMLElement) =>
    {
      const choose_list: HTMLElement | null = this.getChooseList(modal);

      if (!choose_list)
      {
        return;
      }

      this.clearChooseList(choose_list);
    });
  }

  /**
   * // Method: 選択リストを空にして非表示にする
   */
  private clearChooseList(choose_list: HTMLElement): void
  {
    choose_list.replaceChildren();
    choose_list.hidden = true;
    choose_list.toggleAttribute('aria-hidden', true);
    this.clearChooseListScrollState(choose_list);
  }

  /**
   * // Method: choose list の scroll 状態更新イベントを登録する
   */
  private bindChooseListScrollState(choose_list: HTMLElement): void
  {
    if (this.mScrollStateListeners.has(choose_list))
    {
      return;
    }

    const scroll_listener = (): void =>
    {
      this.syncChooseListScrollPosition(choose_list);
    };

    choose_list.addEventListener('scroll', scroll_listener, { passive: true });
    this.mScrollStateListeners.set(choose_list, scroll_listener);
  }

  /**
   * // Method: choose list の scroll 状態更新イベントを解除する
   */
  private unbindChooseListScrollState(choose_list: HTMLElement): void
  {
    const scroll_listener: (() => void) | undefined = this.mScrollStateListeners.get(choose_list);

    if (!scroll_listener)
    {
      return;
    }

    choose_list.removeEventListener('scroll', scroll_listener);
    this.mScrollStateListeners.delete(choose_list);
  }

  /**
   * // Method: choose list の表示数に応じて中央寄せ / スクロール案内状態を切り替える
   * // Note:
   * // - item数が itemsPerView 以下の場合は中央寄せにします。
   * // - item数が itemsPerView を超える場合は横スクロール可能として edge hint を有効にします。
   */
  private syncChooseListScrollState(choose_list: HTMLElement, item_count: number): void
  {
    const choose_area: HTMLElement | null = this.getChooseAreaByList(choose_list);
    const scroll_state: 'fit' | 'overflow' =
      item_count > this.mOptions.list.itemsPerView ? 'overflow' : 'fit';

    choose_list.setAttribute(ModalContentChooser.ATTR_CHOOSE_SCROLL_STATE, scroll_state);
    choose_area?.setAttribute(ModalContentChooser.ATTR_CHOOSE_SCROLL_STATE, scroll_state);

    if (scroll_state === 'fit')
    {
      choose_list.removeAttribute(ModalContentChooser.ATTR_CHOOSE_SCROLL_POSITION);
      choose_area?.removeAttribute(ModalContentChooser.ATTR_CHOOSE_SCROLL_POSITION);
      choose_list.scrollLeft = 0;
      return;
    }

    this.syncChooseListScrollPosition(choose_list);

    if (typeof window !== 'undefined')
    {
      window.requestAnimationFrame(() =>
      {
        this.syncChooseListScrollPosition(choose_list);
      });
    }
  }

  /**
   * // Method: choose list の現在スクロール位置を edge hint 用の状態へ反映する
   */
  private syncChooseListScrollPosition(choose_list: HTMLElement): void
  {
    const choose_area: HTMLElement | null = this.getChooseAreaByList(choose_list);

    if (choose_list.getAttribute(ModalContentChooser.ATTR_CHOOSE_SCROLL_STATE) !== 'overflow')
    {
      choose_list.removeAttribute(ModalContentChooser.ATTR_CHOOSE_SCROLL_POSITION);
      choose_area?.removeAttribute(ModalContentChooser.ATTR_CHOOSE_SCROLL_POSITION);
      return;
    }

    const max_scroll_left: number = Math.max(0, choose_list.scrollWidth - choose_list.clientWidth);
    const current_scroll_left: number = Math.min(
      Math.max(0, choose_list.scrollLeft),
      max_scroll_left,
    );
    const threshold: number = 2;
    let scroll_position: 'start' | 'middle' | 'end' = 'middle';

    if (max_scroll_left <= threshold)
    {
      scroll_position = 'start';
    }
    else if (current_scroll_left <= threshold)
    {
      scroll_position = 'start';
    }
    else if (current_scroll_left >= max_scroll_left - threshold)
    {
      scroll_position = 'end';
    }

    choose_list.setAttribute(ModalContentChooser.ATTR_CHOOSE_SCROLL_POSITION, scroll_position);
    choose_area?.setAttribute(ModalContentChooser.ATTR_CHOOSE_SCROLL_POSITION, scroll_position);
  }

  /**
   * // Method: choose list の中央寄せ / スクロール案内状態を解除する
   */
  private clearChooseListScrollState(choose_list: HTMLElement): void
  {
    const choose_area: HTMLElement | null = this.getChooseAreaByList(choose_list);

    choose_list.removeAttribute(ModalContentChooser.ATTR_CHOOSE_SCROLL_STATE);
    choose_list.removeAttribute(ModalContentChooser.ATTR_CHOOSE_SCROLL_POSITION);
    choose_area?.removeAttribute(ModalContentChooser.ATTR_CHOOSE_SCROLL_STATE);
    choose_area?.removeAttribute(ModalContentChooser.ATTR_CHOOSE_SCROLL_POSITION);
  }

  /**
   * // Method: choose list を内包する表示領域を取得する
   */
  private getChooseAreaByList(choose_list: HTMLElement): HTMLElement | null
  {
    return choose_list.closest<HTMLElement>(ModalContentChooser.SELECTOR_CHOOSE_AREA);
  }

  /**
   * // Method: 現在 item の group が選択リスト表示対象か判定する
   */
  private shouldShowByItem(content_item: ModalContentItem): boolean
  {
    return this.isEffectiveChooserViewer() && this.isGroupEnabled(content_item.group_name);
  }

  /**
   * // Method: group 内 item 数から選択リストを非表示にするか判定する
   */
  private shouldHideByItemCount(item_count: number): boolean
  {
    if (item_count === 0)
    {
      return true;
    }

    return this.mOptions.hideWhenSingleItem && item_count <= 1;
  }

  /**
   * // Method: group 名が選択リスト有効対象か判定する
   * // Note:
   * // - disabledGroups に含まれる group は常に無効です。
   * // - enabledGroups が指定されている場合、その中に含まれる group だけ有効です。
   * // - enabledGroups が空の場合は、disabledGroups 以外の group を有効として扱います。
   */
  private isGroupEnabled(group_name: string): boolean
  {
    if (!this.isEffectiveChooserViewer())
    {
      return false;
    }

    if (this.mOptions.disabledGroups.includes(group_name))
    {
      return false;
    }

    if (this.mOptions.enabledGroups.length === 0)
    {
      return true;
    }

    return this.mOptions.enabledGroups.includes(group_name);
  }

  /**
   * // Method: 現在の choose list UI 有効状態を判定する
   */
  private isEffectiveChooserViewer(): boolean
  {
    return this.mOptions.mode === 'chooser-viewer';
  }

  /**
   * // Method: 選択リストの描画先を取得する
   */
  public getChooseList(modal: HTMLElement | null): HTMLElement | null
  {
    if (!modal)
    {
      return null;
    }

    return modal.querySelector<HTMLElement>(ModalContentChooser.SELECTOR_CHOOSE_LIST);
  }

  /**
   * // Method: choose item から表示対象の content item を取得する
   * // Note:
   * // - group / index が付いている場合は、それを優先して item を直接解決します。
   */
  private getContentItemByChooseItem(choose_item: HTMLElement): ModalContentItem | null
  {
    const group_name: string | null = choose_item.getAttribute(ModalContentChooser.ATTR_CONTENT_GROUP);
    const item_index: number | null = this.getItemIndexByChooseItem(choose_item);

    if (group_name && item_index !== null)
    {
      return this.mContentCollection.getItemByPosition(group_name, item_index);
    }

    return this.mContentCollection.getItemByElement(choose_item);
  }

  /**
   * // Method: choose item から item index を取得する
   */
  private getItemIndexByChooseItem(choose_item: HTMLElement): number | null
  {
    const index_value: string | null = choose_item.getAttribute(ModalContentChooser.ATTR_CONTENT_INDEX);

    if (index_value === null)
    {
      return null;
    }

    const item_index: number = Number(index_value);

    return Number.isNaN(item_index) ? null : item_index;
  }

  /**
   * // Method: 選択リスト用の li 要素を作成する
   */
  private createListItem(
    content_item: ModalContentItem,
    current_item: ModalContentItem,
  ): HTMLLIElement
  {
    const list_item: HTMLLIElement = document.createElement('li');
    const choose_button: HTMLButtonElement = this.createChooseButton(content_item, current_item);

    list_item.classList.add(ModalContentChooser.CLASS_LIST_ITEM);
    list_item.setAttribute(ModalContentChooser.ATTR_CHOOSE_LIST_ITEM, '');
    list_item.appendChild(choose_button);

    if (this.isSameItem(content_item, current_item))
    {
      list_item.setAttribute(ModalContentChooser.ATTR_CHOOSE_ITEM_STATE, 'current');
    }

    return list_item;
  }

  /**
   * // Method: 選択リスト用の button 要素を作成する
   */
  private createChooseButton(
    content_item: ModalContentItem,
    current_item: ModalContentItem,
  ): HTMLButtonElement
  {
    const choose_button: HTMLButtonElement = document.createElement('button');
    const is_current: boolean = this.isSameItem(content_item, current_item);

    choose_button.type = 'button';
    choose_button.classList.add(ModalContentChooser.CLASS_CHOOSE_BUTTON);
    choose_button.setAttribute(ModalContentChooser.ATTR_CHOOSE_ITEM, '');
    choose_button.setAttribute(ModalContentChooser.ATTR_CONTENT_GROUP, content_item.group_name);
    choose_button.setAttribute(ModalContentChooser.ATTR_CONTENT_INDEX, String(content_item.item_index));
    choose_button.toggleAttribute('aria-current', is_current);

    if (is_current)
    {
      choose_button.setAttribute(ModalContentChooser.ATTR_CHOOSE_ITEM_STATE, 'current');
    }

    choose_button.appendChild(this.cloneChooseContent(content_item));

    return choose_button;
  }

  /**
   * // Method: 選択リストに表示する content を複製する
   * // Note:
   * // - data-modal-choose-source があればそれを優先し、なければ data-modal-content-source を使います。
   * // - 元DOMを壊さないように、必ず clone した node を返します。
   */
  private cloneChooseContent(content_item: ModalContentItem): Node
  {
    const choose_content: HTMLElement = content_item.choose_content ?? content_item.content;

    if (choose_content instanceof HTMLTemplateElement)
    {
      return choose_content.content.cloneNode(true);
    }

    const cloned_content: HTMLElement = choose_content.cloneNode(true) as HTMLElement;
    cloned_content.removeAttribute('hidden');
    cloned_content.removeAttribute('aria-hidden');

    return cloned_content;
  }

  /**
   * // Method: 2つの content item が同一か判定する
   */
  private isSameItem(first_item: ModalContentItem, second_item: ModalContentItem): boolean
  {
    return first_item.group_name === second_item.group_name
      && first_item.item_index === second_item.item_index;
  }
}
